import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface TerminologyMap {
  deal: string;
  deals: string;
  carrier: string;
  broker: string;
  rate: string;
  lane: string;
  pickup: string;
  delivery: string;
  bol: string;
  invoice: string;
  rate_confirmation: string;
  agent_broker: string;
  agent_carrier: string;
  onboarding_path: string;
  onboarding_title: string;
  [key: string]: string;
}

interface AgentTemplate {
  key: string;
  label: string;
  fields: string[];
}

interface DealType {
  key: string;
  label: string;
}

interface DocumentType {
  key: string;
  label: string;
}

export interface VerticalConfig {
  slug: string;
  name: string;
  status: string;
  terminology: TerminologyMap;
  agentTemplates: AgentTemplate[];
  dealTypes: DealType[];
  documentTypes: DocumentType[];
  trackingStatuses: string[];
}

interface VerticalContextType {
  vertical: VerticalConfig | null;
  loading: boolean;
  termMap: (genericTerm: string) => string;
  agentTemplates: AgentTemplate[];
  dealTypes: DealType[];
  documentTypes: DocumentType[];
  trackingStatuses: string[];
  isPreview: boolean;
}

const defaultTerminology: TerminologyMap = {
  deal: "Deal", deals: "Deals", carrier: "Carrier", broker: "Broker",
  rate: "Rate", lane: "Lane", pickup: "Pickup", delivery: "Delivery",
  bol: "BOL", invoice: "Invoice", rate_confirmation: "Rate Confirmation",
  agent_broker: "Broker Agent", agent_carrier: "Carrier Agent",
  onboarding_path: "/carriers", onboarding_title: "Carrier Onboarding",
};

/** Shape of rows in the industry_verticals table (not yet in generated Supabase types). */
interface IndustryVerticalRow {
  slug: string;
  name: string;
  status: string;
  terminology: TerminologyMap | null;
  agent_templates: AgentTemplate[] | null;
  deal_types: DealType[] | null;
  document_types: DocumentType[] | null;
  tracking_statuses: string[] | null;
}

const VerticalContext = createContext<VerticalContextType>({
  vertical: null, loading: true,
  termMap: (t) => t,
  agentTemplates: [], dealTypes: [], documentTypes: [], trackingStatuses: [],
  isPreview: false,
});

export function VerticalProvider({ children }: { children: ReactNode }) {
  const { companyId } = useAuth();
  const [vertical, setVertical] = useState<VerticalConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }

    (async () => {
      const { data: company } = await supabase
        .from("companies").select("vertical").eq("id", companyId).single();
      const slug = company?.vertical || "freight";

      // industry_verticals may not exist yet; fall back to freight defaults.
      // Use maybeSingle() so a missing row returns data:null without a 406.
      try {
        const { data: v, error } = await (supabase
          .from("industry_verticals") as any)
          .select("*")
          .eq("slug", slug)
          .maybeSingle() as unknown as { data: IndustryVerticalRow | null; error: unknown };

        if (v && !error) {
          setVertical({
            slug: v.slug,
            name: v.name,
            status: v.status,
            terminology: v.terminology || defaultTerminology,
            agentTemplates: v.agent_templates || [],
            dealTypes: v.deal_types || [],
            documentTypes: v.document_types || [],
            trackingStatuses: v.tracking_statuses || [],
          });
        } else {
          // Table doesn't exist or no matching row · use freight defaults
          setVertical({
            slug,
            name: slug.charAt(0).toUpperCase() + slug.slice(1),
            status: "active",
            terminology: defaultTerminology,
            agentTemplates: [],
            dealTypes: [
              { key: "freight_load", label: "Freight Load" },
              { key: "spot_rate", label: "Spot Rate" },
              { key: "contract", label: "Contract" },
            ],
            documentTypes: [
              { key: "bol", label: "Bill of Lading" },
              { key: "rate_con", label: "Rate Confirmation" },
              { key: "insurance_cert", label: "Insurance Certificate" },
            ],
            trackingStatuses: ["booked", "dispatched", "in_transit", "delivered", "completed"],
          });
        }
      } catch {
        // Network or table-missing error · use defaults silently
        setVertical({
          slug,
          name: slug.charAt(0).toUpperCase() + slug.slice(1),
          status: "active",
          terminology: defaultTerminology,
          agentTemplates: [],
          dealTypes: [
            { key: "freight_load", label: "Freight Load" },
            { key: "spot_rate", label: "Spot Rate" },
            { key: "contract", label: "Contract" },
          ],
          documentTypes: [
            { key: "bol", label: "Bill of Lading" },
            { key: "rate_con", label: "Rate Confirmation" },
            { key: "insurance_cert", label: "Insurance Certificate" },
          ],
          trackingStatuses: ["booked", "dispatched", "in_transit", "delivered", "completed"],
        });
      }
      setLoading(false);
    })();
  }, [companyId]);

  const termMap = useCallback((genericTerm: string): string => {
    if (!vertical?.terminology) return genericTerm;
    const key = genericTerm.toLowerCase().replace(/\s+/g, "_");
    return vertical.terminology[key] || genericTerm;
  }, [vertical]);

  return (
    <VerticalContext.Provider value={{
      vertical, loading, termMap,
      agentTemplates: vertical?.agentTemplates || [],
      dealTypes: vertical?.dealTypes || [],
      documentTypes: vertical?.documentTypes || [],
      trackingStatuses: vertical?.trackingStatuses || [],
      isPreview: vertical?.status === "preview",
    }}>
      {children}
    </VerticalContext.Provider>
  );
}

export const useVertical = () => useContext(VerticalContext);
